import { FC, useEffect, useRef, useState } from 'react';
import type { Study } from '@/model/Study.model.ts';
import { Cage, CageApiId } from '@/model/Cage.model.ts';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useAlerts, useChangeAnimalActions, useSelectedCage, useSubjects } from '@/utils/workflow/useWorkflow.tsx';
import { notAborted } from '@/support/Hooks/fetch/useAbortController.ts';
import {
  CageConsumptionMetricApiId,
  ConsumptionMetric,
  ConsumptionMetricEdit,
  ConsumptionMetricStatus,
  ConsumptionType,
} from '@/model/Consumption.model.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { getFirstAnimal, getNextNonEmptyCage } from '@/components/Workflow/Show/Show.utils.ts';
import { WorkflowSetup } from '@/components/Workflow/Show/Workflow.model.ts';
import ConsumptionMeasurements from '@/components/Workflow/Show/Views/Consumption/ConsumptionMeasurements.tsx';
import { DeleteConsumptionRequest } from '@/components/Workflow/Show/Views/Consumption/ConsumptionActions.tsx';
import { WorkflowNoCageDataCard } from '@/components/Workflow/Show/Views/Consumption/WorkflowNoCageData.tsx';
import { ApiService } from '@/support/ApiService.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import Select from 'react-select';
import { intakeLabels } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import IntakeView from '@/components/Workflow/Show/Views/Consumption/IntakeView.tsx';
import { AnimalAlertV1 } from '@/model/Alert.model.ts';
import { AlertService } from '@/utils/alerts/useAlert.ts';
import { TimePickerNative } from '@/components/UI/TimePickerNative/TimePickerNative';
import { TimeFormat, TimeUtils } from '@/utils/Date.utils';

interface WorkflowConsumptionProps {
  study: Study;
  collections: Array<Cage>;
  workflowSetup: WorkflowSetup;
  focusSearch: () => void;
}

interface CageIntakeData {
  data: Array<ConsumptionMetric> | undefined;
  status: ConsumptionMetricStatus;
  enabled: boolean;
}

export type EditConsumptionRequest = {
  variables: ConsumptionMetricEdit;
};

export const cageAlertSettings = {
  displayHeader: false,
  displayDeferControl: false,
  displayTriggeredBy: false,
  displayActionsBelow: false,
  displayResolveControl: false,
};

const WorkflowConsumption: FC<WorkflowConsumptionProps> = ({ study, collections, workflowSetup, focusSearch }) => {
  const { selectedCage } = useSelectedCage();
  const { changeAnimal, changeCage } = useChangeAnimalActions();
  const { workflowTabConfig, foodIntakeEnabled, fluidIntakeEnabled, intakeTimeEnabled, measured_at } = workflowSetup;
  const { availableSubjects } = useSubjects();
  const { updateAlerts } = useAlerts();
  const [selectedCageApiId, setSelectedCageApiId] = useState<CageApiId>();
  const selectedCageApiIdRef = useRef(selectedCageApiId);

  useEffect(() => {
    selectedCageApiIdRef.current = selectedCageApiId;
  }, [selectedCageApiId]);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [graphDisplay, setGraphDisplay] = useState<ConsumptionType>('food');
  const [time, setTime] = useState<string>(TimeUtils.currentTimeInUserTimezone());
  const [cageIntakeData, setCageIntakeData] = useState<Record<ConsumptionType, CageIntakeData>>({
    food: { data: undefined, status: 'end', enabled: foodIntakeEnabled },
    fluid: { data: undefined, status: 'end', enabled: fluidIntakeEnabled },
  });

  useEffect(() => {
    setCageIntakeData((prevState) => ({
      ...prevState,
      food: {
        ...prevState.food,
        enabled: foodIntakeEnabled,
      },
      fluid: {
        ...prevState.fluid,
        enabled: fluidIntakeEnabled,
      },
    }));
    setGraphDisplay(foodIntakeEnabled ? 'food' : 'fluid');
  }, [foodIntakeEnabled, fluidIntakeEnabled]);

  useEffect(() => {
    if (_isNotEmpty(selectedCage)) {
      const cageId = collections.find((cage: Cage) => cage.id === selectedCage)?.api_id as CageApiId;
      setSelectedCageApiId(cageId);
      if (!intakeTimeEnabled) {
        setTime(TimeUtils.currentTimeInUserTimezone());
      }
    }
  }, [collections, availableSubjects, selectedCage, intakeTimeEnabled, measured_at]);

  const { invoke: fetchFoodIntakeData } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption',
    invokeOnInit: false,
    path: { studyApiId: study.api_id, cageApiId: selectedCageApiId as CageApiId },
    query: { type: 'food' },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'food-consumption-fetch' } },
  });

  const { invoke: fetchFluidIntakeData } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption',
    invokeOnInit: false,
    path: { studyApiId: study.api_id, cageApiId: selectedCageApiId as CageApiId },
    query: { type: 'fluid' },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fluid-consumption-fetch' } },
  });

  const onSave: Record<string, () => void> = {
    'focus-search': () => focusSearch(),
    'first-subject': () => {
      const firstAnimal = getFirstAnimal(selectedCage, availableSubjects);
      changeAnimal({ type: 'SELECT_ANIMAL', animalId: firstAnimal.id });
    },
    'next-cage': () => {
      const nextCage = getNextNonEmptyCage(selectedCage, collections, availableSubjects);
      if (nextCage.id === selectedCage) {
        // Staying on same cage (no more non-empty cages ahead), refresh data
        initialise();
      } else {
        // Moving to different cage, trigger navigation (useEffect will handle refresh)
        changeCage({ type: 'SELECT_CAGE', cageId: nextCage.id });
      }
    },
  };

  const initialise = async () => {
    const fetchData = async (type: ConsumptionType) => {
      if (cageIntakeData[type].enabled || graphDisplay === type) {
        try {
          const response = type === 'food' ? await fetchFoodIntakeData() : await fetchFluidIntakeData();
          if (response.type === 'success') {
            const data = response.body.data as Array<ConsumptionMetric>;
            const status = data?.[0]?.status ?? 'end';
            setCageIntakeData((prevState) => ({
              ...prevState,
              [type]: {
                ...prevState[type],
                data,
                status,
              },
            }));
          }
        } catch (apiError) {
          if (notAborted(apiError)) {
            setApiError(apiError);
          }
        }
      }
    };

    await Promise.all([fetchData('food'), fetchData('fluid')]);
  };

  const handleSave = () => {
    const onSaveAction = workflowTabConfig?.[4]?.onSave;
    onSave?.[onSaveAction]?.();
    if (onSaveAction === 'focus-search') {
      initialise();
    }
  };

  useEffect(() => {
    if (_isNotEmpty(selectedCageApiId)) {
      initialise();
    }
  }, [selectedCageApiId, foodIntakeEnabled, fluidIntakeEnabled]);

  const handleExcludeMetric = (consumptionMetricApiId: CageConsumptionMetricApiId) => {
    if (_notNil(selectedCageApiId)) {
      const cageAtCallTime = selectedCageApiId;
      ApiService.call({
        endpoint:
          'PATCH /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption/{cageConsumptionMetricApiId}/exclude',
        path: {
          studyApiId: study.api_id,
          cageApiId: cageAtCallTime,
          cageConsumptionMetricApiId: consumptionMetricApiId,
        },
      })
        .then(() => {
          successToast(`Successfully excluded ${graphDisplay} intake measurement`);
          if (selectedCageApiIdRef.current === cageAtCallTime) {
            initialise();
          }
        })
        .catch(() => errorToast(`There was a problem excluding the ${graphDisplay} intake measurement`));
    }
  };

  const handleEditMetric = async (
    consumptionMetricApiId: CageConsumptionMetricApiId,
    request: EditConsumptionRequest
  ) => {
    if (_notNil(selectedCageApiId)) {
      const cageAtCallTime = selectedCageApiId;
      return await ApiService.call({
        endpoint: 'PATCH /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption/{cageConsumptionMetricApiId}',
        path: {
          studyApiId: study.api_id,
          cageApiId: cageAtCallTime,
          cageConsumptionMetricApiId: consumptionMetricApiId,
        },
        body: request.variables,
      }).then((editResponse) => {
        if (editResponse.type === 'success') {
          const alerts = (editResponse.body as ConsumptionMetric)?.animal_alerts as Array<AnimalAlertV1>;
          if (_isNotEmpty(alerts)) {
            handleAlerts(alerts);
          }
        }
        if (selectedCageApiIdRef.current === cageAtCallTime) {
          initialise();
        }
      });
    }
  };

  const handleDeleteMetric = async (
    consumptionMetricApiId: CageConsumptionMetricApiId,
    request: DeleteConsumptionRequest
  ) => {
    if (_notNil(selectedCageApiId)) {
      const cageAtCallTime = selectedCageApiId;
      return await ApiService.call({
        endpoint: 'DELETE /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption/{cageConsumptionMetricApiId}',
        path: {
          studyApiId: study.api_id,
          cageApiId: cageAtCallTime,
          cageConsumptionMetricApiId: consumptionMetricApiId,
        },
        body: request.variables,
      })
        .then(() => {
          successToast(`Successfully deleted ${graphDisplay} intake measurement`);
          if (selectedCageApiIdRef.current === cageAtCallTime) {
            initialise();
          }
        })
        .catch(() => errorToast(`There was a problem deleting the ${graphDisplay} intake measurement`));
    }
  };

  const handleAlerts = (alerts: Array<AnimalAlertV1>) => {
    const unresolvedAlerts = alerts.filter((alert) => _isNil(alert.resolved_at));
    if (_isNotEmpty(unresolvedAlerts)) {
      updateAlerts({ type: 'ADD_ALERTS', newAlerts: unresolvedAlerts as Array<AnimalAlertV1> });
      // animalApiId is intentionally omitted: cage-level aggregate alerts show N animals,
      // so per-animal resolution is not supported from this surface.
      AlertService.createAlerts({
        alerts: unresolvedAlerts.slice(0, 1) as Array<AnimalAlertV1>,
        animalName: `${unresolvedAlerts.length} animals`,
        cageName: collections.find((cage) => cage.api_id === selectedCageApiId)?.name ?? '',
        settings: cageAlertSettings,
        calculations: study.settings.calculations,
        studyApiId: study.api_id,
      });
    }
  };

  const CageDropdown = (
    <Select
      className="ui__select w5"
      classNamePrefix="ui__select"
      onChange={(result) => result && setGraphDisplay(result.value)}
      value={{
        value: graphDisplay,
        label: intakeLabels[graphDisplay].graphDisplay,
      }}
      options={[
        {
          value: 'food' as ConsumptionType,
          label: 'Cage Food Intake',
        },
        {
          value: 'fluid' as ConsumptionType,
          label: 'Cage Fluid Intake',
        },
      ]}
    />
  );

  return (
    <div className="w-60 bg-light-gray flex flex-column" style={{ minHeight: '100%' }}>
      <div className="bb b--moon-gray pl3 pr4 pt2 pb2 bg-white flex justify-between items-center">
        <span className="basier-med lh-title f6 near-black flex items-center" style={{ minHeight: '2.45em' }}>
          Food and Fluid Intake
        </span>
        {intakeTimeEnabled && (
          <TimePickerNative
            value={time}
            onChange={setTime}
            format={TimeFormat.Time_24HR}
            onKeyDown={(event) => event.preventDefault()}
          />
        )}
      </div>
      {apiError ? (
        <ApiErrorBanner
          className="mw6 mb4"
          error={apiError}
          title="There was an error fetching study data"
          text="An error has occurred when fetching study data, please try again. If this keeps occurring please contact support."
        />
      ) : null}
      <div className="tab-content ui__workflow__expand">
        <div className="flex flex-wrap justify-between items-stretch h-100">
          <div className="w-50 pv2 ph3 br b--moon-gray bg-white">
            {_isNotEmpty(selectedCageApiId) && (
              <IntakeView
                studyApiId={study.api_id}
                studyCalculations={study.settings.calculations}
                selectedCageApiId={selectedCageApiId}
                cageIntakeData={cageIntakeData}
                handleSave={handleSave}
                handleAlerts={handleAlerts}
                selectedTime={time}
              />
            )}
          </div>
          <div className="w-50 bg-white" data-testid={`food-intake-sidebar`}>
            {(_isNotEmpty(cageIntakeData.food.data) || _isNotEmpty(cageIntakeData.fluid.data)) && (
              <div className="flex flex-wrap justify-between items-center pa3">{CageDropdown}</div>
            )}
            {_isNotEmpty(cageIntakeData[graphDisplay].data) ? (
              <ConsumptionMeasurements
                cageConsumptionData={cageIntakeData[graphDisplay].data || []}
                onExcludeClick={handleExcludeMetric}
                onEditClick={handleEditMetric}
                onDeleteClick={handleDeleteMetric}
              />
            ) : (
              <div
                className={_isNotEmpty(cageIntakeData.food.data) || _isNotEmpty(cageIntakeData.fluid.data) ? '' : 'mt4'}
              >
                {WorkflowNoCageDataCard}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowConsumption;
